<script setup lang="ts">
import BannersText from '@/widgets/home/Banners/BannersText.vue'
import BannersList from '@/widgets/home/Banners/BannersList.vue'
import { useRecommendationsBannersQuery, type Audience } from '@/queries/recommendations'
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import DeferredContent from 'primevue/deferredcontent'

import {
  Autoplay,
  Controller,
  FreeMode,
  Navigation,
  Virtual,
  Pagination,
  Mousewheel
} from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/pagination";
import { Swiper, SwiperSlide, useSwiper } from "swiper/vue";
import AiTipForCapsules from '@/components/UI/CustomAITipComponets/CustomPopoverWithScopeIcon.vue'
import BannerTextContentWithTest from '@/widgets/home/Banners/BannerTextContentWithTest.vue'
import { useDevice } from '@/composables/useDevice'
import BannersSkeletons from '@/widgets/home/Banners/BannersSkeletons.vue'

const router = useRouter();
const route = useRoute();
const { isDevice } = useDevice();

const DEFAULT_AUDIENCE: Audience = 'woman'
const audience = computed<Audience>(() => {
  const queryValue = route.query.audience
  if (Array.isArray(queryValue)) {
    return (queryValue[0] ?? DEFAULT_AUDIENCE) as Audience
  }
  return (queryValue ?? DEFAULT_AUDIENCE) as Audience
})

const swiperModules = [FreeMode, Autoplay, Navigation, Controller, Virtual, Pagination, Mousewheel];

const bannersQuery = useRecommendationsBannersQuery(audience)
const isBannersLoading = computed(() => bannersQuery.isLoading.value)
const bannersData = computed(() => bannersQuery.data.value?.data ?? [])

const deviceType = computed(() => isDevice.value);

function handleBannerClick(bannerId:number) {
  router.push({
    name: 'recommendation-banners',
    params: { bannerId: String(bannerId) },
    query: { audience: audience.value },
  });
}

async function loadBannersData() {
  await bannersQuery.refresh();
}
</script>

<template>
  <DeferredContent
    :pt="{root:{style:{
    display:'flex',
    justifyContent:'center',
    flexDirection:'column',
    alignItems:'center',
    paddingTop:'20px',
  }}}" @load="loadBannersData">

    <banners-text />
    <template v-if="isBannersLoading">
      <banners-skeletons />
    </template>


    <template v-else>
      <div v-if="deviceType == 'desktop' || deviceType == 'smallDesktop'"
           class="recommendation_content_wrapper">
        <banners-list
          class="desktop"
          :bannersData="bannersData"
          :is-banners-loading="isBannersLoading"
          @trigger-load-banners-data="loadBannersData"
          @trigger-banner-click="handleBannerClick"
        />
      </div>

      <div v-else
        class="mobile-banners-wrapper"
      >
        <swiper
          :free-mode="true"
          :space-between="20"
          :slides-offset-after="20"
          :pagination="{
          clickable: true,
        }"
          :mousewheel="{
          forceToAxis: true,
          sensitivity: 1,
          releaseOnEdges: true,
        }"
          :breakpoints="{
          360: {
            slidesPerView: 1.25,
          },
          768:{
            slidesPerView: 2.5,
          },
          1024: {
            slidesPerView: 3.25,
          }
        }"
          :modules="swiperModules"
          class="mobile-banners-container"
        >
          <swiper-slide
            v-for="(banner, index) in bannersData"
            :key="index"
            class="company-carousel__item"
            @click="handleBannerClick(banner.id)"
          >
            <div class="left-banner-item">
              <div class="wrapper-for-ai-tips">
                <ai-tip-for-capsules>
                  <span>{{ banner.ai_notes[0] }}</span>
                </ai-tip-for-capsules>
              </div>
              <div class="banners-item-image">
                <img :src="banner.imageUrl" />
              </div>
              <div class="banners-content">
                <div class="banners-item-title h2">
                  {{ banner.name }}
                </div>
                <div class="banners-text body">
                  {{ banner.description }}
                </div>
              </div>
            </div>
          </swiper-slide>
        </swiper>
        <banner-text-content-with-test
          title="Define your style"
          description="Find out your taste preferences"
        />
      </div>
    </template>
  </DeferredContent>
</template>

<style scoped>
.recommendation_content_wrapper {
  display: flex;
  flex-direction: column;
  width: 90%;
  justify-content: center;
  align-items: center;
  gap: 20px;
  border-radius: 40px;
}

.mobile {
  display: none;
}

.company-carousel__item {
  max-height: 550px;
  cursor: grab;
}

.company-carousel__item:active {
  cursor: grabbing;
}

.adv-images__wrapper {
  position: relative;
}

.wrapper-for-ai-tips {
  width: 100%;
  height: 100%;
  padding: 30px;
  position: absolute;
}

.mobile-banners-container {
  width: 100%;
  padding: 10px 0;
  user-select: none;
}

.banners-left-items__wrapper {
  display: flex;
  justify-content: space-around;
  width: 50%;
  flex-wrap: wrap;
  gap: 80px;
}

.left-banner-item {
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  max-height: 450px;
  gap: 12px;
}

.banners-item-image {
  width: 100%;
  height:450px;
  border-radius: 40px;
  overflow: hidden;
}

.banners-item-image img {
  object-fit: cover;
  height:100%;
  width:100%;
}

.banners-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  align-items: flex-start;
  position: absolute;
  bottom: 0;
  padding:10px;
  background: var(--primary-blue-8);
  border-bottom-left-radius: 40px;
  border-bottom-right-radius: 40px;
  width: 100%;
  min-height: 130px;
}

.banners-item-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0 20px;
  color: white;
}

.banners-text {
  padding: 0 20px;
  color: white;
}

:deep(.swiper-pagination-bullet) {
  width: 24px;
  height: 4px;
  border-radius: 100px;
}

.mobile-banners-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-top:20px;
  padding-left: 5%;
  width:100%;
}

@media (max-width: 1024px) {
  .desktop {
    display: none;
  }


  .mobile-banners-container {
    width:100%;
    padding: 0 0 0 16px;
  }
}
</style>